const en = {
  common: {
    nav: { services: "Services", about: "About", guides: "Guides", contact: "Contact" },
    cta: "Start a project",
    language: "Language",
    themeToLight: "Switch to light mode",
    themeToDark: "Switch to dark mode",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    optional: "optional",
    reply: "We reply within 7 hours.",
    scroll: "Scroll",
  },
  footer: {
    tagline: "AI automation, websites and apps for teams that want the busywork handled by systems.",
    company: "Company",
    policies: "Policies",
    elsewhere: "Elsewhere",
    location: "Bikaner, Rajasthan, India · Working worldwide",
    legal: {
      terms: "Terms & Conditions",
      privacy: "Privacy Policy",
      refund: "Refund Policy",
      return: "Return Policy",
      cancellation: "Cancellation Policy",
      shipping: "Shipping Policy",
    },
  },
  cookie: {
    title: "Cookies, with your permission",
    body: "We use essential cookies to run this site. With your consent we also use analytics cookies to improve it.",
    accept: "Accept all",
    reject: "Essential only",
    policy: "Privacy policy",
  },
  cta: {
    title: "Have a process that should run itself?",
    body: "Tell us what you need. We reply within 7 hours.",
    primary: "Start a conversation",
    secondary: "Explore services",
  },
  home: {
    hero: {
      eyebrow: "AI automation & software studio",
      titleA: "We build systems",
      titleB: "that work for you.",
      lede: "AI agents, automations, websites and apps — designed, built and shipped end to end.",
      primary: "Start a project",
      secondary: "See our work",
      tags: ["AI agents", "Automation", "Web apps", "Mobile apps", "Data pipelines", "AI video"],
    },
    marquee: ["AI agents", "RAG systems", "Workflow automation", "Websites", "Web apps", "Mobile apps", "Web scraping", "AI video", "Python automation", "n8n"],
    stats: [
      { value: 4, suffix: "+", label: "years building AI systems" },
      { value: 100, suffix: "+", label: "workflows automated" },
      { value: 7, suffix: "h", label: "maximum reply time" },
    ],
    services: {
      label: "What we do",
      title: "Four ways we help.",
      more: "All services",
    },
    work: {
      label: "Selected work",
      title: "Live projects we have shipped.",
      intro: "Open any card to see the real thing.",
      nextLabel: "Your project",
      nextTitle: "Could be next.",
      nextBody: "Websites, apps and automation, shipped end to end.",
      nextCta: "Tell us about it",
      visit: "open live site in a new tab",
    },
    process: {
      label: "How we work",
      title: "Idea to launch in four steps.",
      steps: [
        { title: "Discover", desc: "A free call to understand your goal." },
        { title: "Scope", desc: "A clear written plan and timeline." },
        { title: "Build", desc: "Working demos at every milestone." },
        { title: "Launch", desc: "Go live, with support after." },
      ],
    },
    testimonials: { label: "Client words", title: "What our clients say." },
  },
  services: {
    eyebrow: "Services",
    title: "Focused services, built around your goal.",
    lede: "Every project starts with a free discovery call.",
    timeline: "Typical timeline",
    included: "What's included",
    tools: "Tools we use",
    showTools: "Show tools",
    hideTools: "Hide tools",
    options: "Ways to start",
    choose: "Choose",
    discuss: "Discuss your project",
    extrasTitle: "Also available",
    extras: ["AI consulting", "Architecture design", "API integration", "Cloud & DevOps", "Maintenance & support", "Dedicated engineers"],
    items: {
      "ai-automation": {
        title: "AI Automation",
        tagline: "Agents and assistants that take repetitive work off your team.",
        timeline: "2–12 weeks",
        includes: ["Process mapping", "AI chatbots & agents", "Knowledge assistants (RAG)", "CRM & tool integrations", "30 days of support"],
        plans: [
          { name: "Starter Automation", note: "One workflow · 2–3 weeks" },
          { name: "Business Automation Suite", note: "Multiple workflows · 4–6 weeks" },
          { name: "Enterprise AI Systems", note: "Full integration · 8–12 weeks" },
        ],
      },
      "web-development": {
        title: "Websites & Web Apps",
        tagline: "Fast, modern sites and apps that are easy to run.",
        timeline: "1–10 weeks",
        includes: ["UI/UX design", "Responsive build", "Admin panel", "SEO & performance", "60 days of support"],
        plans: [
          { name: "Static Business Website", note: "5–7 pages · 1–2 weeks" },
          { name: "Dynamic Website with Admin", note: "Admin panel · 3–4 weeks" },
          { name: "Full Custom Web App", note: "Custom features · 6–10 weeks" },
        ],
      },
      "python-automation": {
        title: "Python & Data",
        tagline: "Scripts, scrapers and pipelines that run on their own.",
        timeline: "3 days–4 weeks",
        includes: ["Custom scripts", "Web scraping", "Data processing", "Scheduled jobs", "Logging & docs"],
        plans: [
          { name: "Simple Automation Script", note: "One task · 3–5 days" },
          { name: "Web Scraping Solution", note: "Multi-page · 1–2 weeks" },
          { name: "Complex Data Pipeline", note: "Many sources · 2–4 weeks" },
        ],
      },
      "ai-video": {
        title: "AI Video & Avatars",
        tagline: "Video content produced at volume, without editors.",
        timeline: "2–10 weeks",
        includes: ["Custom avatars", "Voice synthesis", "Script-to-video pipeline", "Brand styling", "Team training"],
        plans: [
          { name: "Basic AI Video Setup", note: "One avatar · 2–3 weeks" },
          { name: "Professional Avatar System", note: "Voice cloning · 4–5 weeks" },
          { name: "Enterprise Video Platform", note: "API & white-label · 6–10 weeks" },
        ],
      },
    },
  },
  about: {
    eyebrow: "About",
    title: "A small team building practical AI.",
    lede: "We replace repetitive work with reliable systems — from one script to a full platform.",
    storyLabel: "Our story",
    story: "We started Optivra Tech to bring modern AI into everyday business. We learn how you work first, then build what fits.",
    valuesLabel: "What guides us",
    values: [
      { title: "Mission", desc: "Measurable growth through practical AI and automation." },
      { title: "Vision", desc: "The technology partner growing businesses rely on." },
      { title: "Commitment", desc: "Quality work, clear communication, real results." },
    ],
    skillsLabel: "Expertise",
    skillsTitle: "What we are good at.",
    skillsHint: "Hover or tap a card to see the tools.",
    skills: [
      { title: "AI & ML", tools: "OpenAI · LangChain · RAG · PyTorch" },
      { title: "Backend", tools: "Python · FastAPI · Node.js · Django" },
      { title: "Frontend", tools: "React · Next.js · TypeScript · Tailwind" },
      { title: "Data", tools: "MongoDB · PostgreSQL · Redis · Pinecone" },
      { title: "Cloud", tools: "AWS · Google Cloud · Azure · Docker" },
      { title: "Automation", tools: "n8n · Selenium · Scrapy · Airflow" },
    ],
    teamLabel: "The team",
    team: ["AI & automation engineers", "Full-stack developers", "Solution architects", "DevOps engineers"],
    whyLabel: "Why us",
    why: [
      { title: "Fast", desc: "Short cycles, working demos." },
      { title: "Current", desc: "Proven, up-to-date tools." },
      { title: "Clean", desc: "Documented, maintainable code." },
    ],
  },
  contact: {
    eyebrow: "Contact",
    title: "Let's talk.",
    lede: "Tell us about your project. We reply within 7 hours.",
    name: "Full name",
    email: "Email",
    phone: "Phone",
    subject: "Subject",
    subjectPh: "e.g. Automate invoice processing",
    message: "Message",
    messagePh: "What do you need, and which tools are involved?",
    send: "Send message",
    sending: "Sending…",
    privacy: "We never share your details.",
    okTitle: "Message sent",
    okBody: "Thanks! We'll reply within 7 hours.",
    errTitle: "Something went wrong",
    errBody: "Your message could not be sent. Please try again.",
    emailLabel: "Email",
    phoneLabel: "Phone",
    hoursLabel: "Hours",
    hours: "Mon–Sat, 9:00–19:00 IST",
  },
  checkout: {
    eyebrow: "Start a project",
    title: "Tell us what you need.",
    lede: "Pick the closest option. We'll confirm scope with you.",
    details: "Your details",
    project: "Project",
    name: "Full name",
    company: "Company",
    email: "Email",
    phone: "Phone",
    service: "Service",
    selectService: "Select a service",
    option: "Option",
    selectOption: "Select an option",
    notes: "Project notes",
    notesPh: "What should it do? Which tools should it connect to?",
    agree: "I agree to the",
    and: "and",
    submit: "Submit project request",
    submitting: "Sending…",
    summary: "Summary",
    summaryService: "Service",
    summaryOption: "Option",
    next: "We reply within 7 hours to confirm scope and next steps.",
    empty: "Select a service and option.",
    perks: ["Secure processing", "7-day refund window", "Reply within 7 hours"],
    refundLink: "Refund policy",
    termsTitle: "Terms required",
    termsBody: "Please accept the terms to continue.",
    okTitle: "Request received",
    okBody: "Thanks! We'll contact you within 7 hours.",
    errTitle: "Something went wrong",
    errBody: "Your request could not be sent. Please try again.",
  },
  guide: {
    eyebrow: "Guides",
    title: "Practical guides from real client work.",
    lede: "Step-by-step playbooks on AI, automation and careers.",
    badges: ["Instant PDF download", "Copy sent by email", "Secure payment via PhonePe"],
    listLabel: "Available guides",
    listTitle: "Choose a guide.",
    mins: "min",
    levels: { "All Levels": "All levels", Beginner: "Beginner", Intermediate: "Intermediate", Advanced: "Advanced" },
    choose: "Choose",
    selected: "Selected",
    items: {
      g0: "A 30–45 day plan to land your dream job: resume, interviews, outreach.",
      g1: "Diffusion models, custom nodes and fast prototyping in ComfyUI.",
      g2: "Build autonomous systems that replace manual business work.",
      g3: "Vector databases, chunking and deploying knowledge agents.",
      g4: "Host n8n and build multi-app flows with webhooks.",
    },
    buyLabel: "Checkout",
    buyTitle: "Get your copy.",
    buySub: "Used for your invoice and to email your download link.",
    name: "Full name",
    phone: "Phone",
    email: "Email",
    country: "Country",
    countries: { India: "India", "United States": "United States", "United Kingdom": "United Kingdom", Canada: "Canada", Australia: "Australia", Germany: "Germany", Other: "Other" },
    gst: "GST number",
    company: "Company name",
    agree: "I agree to the",
    and: "and",
    terms: "Terms & Conditions",
    refund: "Refund Policy",
    pay: "Pay {amount} securely",
    redirecting: "Redirecting to payment…",
    buying: "You're buying",
    format: "PDF guide · digital download",
    price: "Price",
    gstLine: "GST ({percent}%)",
    total: "Total",
    loadingPrice: "Loading price…",
    methods: "Pay with UPI, cards, net banking or wallets.",
    secure: "Payments are processed securely by PhonePe.",
    faqLabel: "FAQ",
    faqTitle: "Common questions.",
    faqs: [
      { q: "What format are the guides in?", a: "A PDF with diagrams, code snippets and step-by-step instructions." },
      { q: "How quickly will I get it?", a: "Instantly. You'll be taken to a download page and get a copy by email." },
      { q: "Can I get a refund?", a: "Guides are instant digital downloads, so refunds are limited. See our Refund Policy." },
      { q: "Who writes the guides?", a: "Our engineers, from the systems they build for clients." },
    ],
    errors: {
      "Full name is required": "Full name is required",
      "Email is required": "Email is required",
      "Please enter a valid email": "Please enter a valid email",
      "Phone number is required": "Phone number is required",
      "Please enter a valid phone number": "Please enter a valid phone number",
      "You must accept the terms": "You must accept the terms",
      "Something went wrong. Please try again.": "Something went wrong. Please try again.",
    },
  },
  success: {
    verifying: "Confirming your payment…",
    pending: "Payment is still processing",
    checking: "Checking again automatically ({n}/10)",
    checkAgain: "Check again",
    paid: "Payment received. Thank you.",
    downloads: "Your downloads",
    dlGuide: "Download guide (PDF)",
    dlInvoice: "Download invoice (PDF)",
    linksNote: "Links stay valid for 24 hours. A copy was also sent to your email.",
    expired: "This download link has expired",
    failed: "We couldn't complete this",
    support: "Contact support",
    home: "Back to home",
    errors: {
      "No order ID found. Please check your payment email for order details.": "No order ID found. Please check your payment email for order details.",
      "Payment failed. Please try again.": "Payment failed. Please try again.",
      "Unable to verify payment status.": "Unable to verify payment status.",
      "Download links have expired after 24 hours.": "Download links have expired after 24 hours.",
      "Unable to fetch download links.": "Unable to fetch download links.",
      "Download failed. Please try again or contact support.": "Download failed. Please try again or contact support.",
      "Something went wrong.": "Something went wrong.",
    },
  },
  legal: {
    notice: "This policy is published in English, which is the legally binding version.",
  },
  work: {
    kke: {
      kind: "Client website",
      summary: "Multilingual catalogue site for an industrial supplier exporting worldwide.",
      highlights: ["English, French & Arabic", "12 product lines", "Quotes & WhatsApp"],
    },
    pokemac: {
      kind: "Our product · macOS app",
      summary: "A playful menu-bar app that reacts to slaps, lid flips and USB plugs.",
      highlights: ["Native macOS", "Runs on-device"],
    },
    drillbuddy: {
      kind: "Our product · Android app",
      summary: "Audio-based reaction drills for wrestling, MMA and badminton.",
      highlights: ["Google Play", "Adjustable intensity"],
    },
    prayer: {
      kind: "Our product · Android app",
      summary: "A calm prayer counter with themes and daily progress.",
      highlights: ["8 languages", "Google Play"],
    },
  },
};

export default en;
